<template>
  <div class="activity-stage">
    <div class="build-a-sentence" ref="buildASentenceDOM">
      <div class="dropdowns" transition="fade-05" v-if="showDropdowns">
        <div class="option-container" v-for="dropdown of currentRound.dropdowns" :key="dropdown.id">
          <dropdown
            :id="dropdown.id"
            :label="dropdown.name"
            :drop-down-id="`${dropdown.id}`"
            :options="dropdown.answers.map(({ answer }) => answer)"
            :enabled="!activeDropdown || dropdown.id === activeDropdown"
            :on-item-select="onDropdownSelect"
            :on-open="onDropdownOpen"
          >
          </dropdown>
        </div>
      </div>

      <div class="complete-sentence" transition="fade-05" v-if="showSentence">
        <div class="left">
          <div class="sentence">
            <blinking-cursor-text :text="sentence" :text-step="0"> </blinking-cursor-text>
          </div>
        </div>
        <div class="right">
          <img class="sentence-image-monochrome" :src="monochromeImage" alt="" />
          <img
            v-show="showColor"
            transition="fade"
            class="sentence-image-color"
            :src="colorImage"
            alt=""
          />
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import Dropdown from 'components/activity/Dropdown.vue';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    BlinkingCursorText,
    Dropdown
  },

  data() {
    return {
      voice: null,
      buildASentenceDOM: null,
      activeDropdown: null,
      showDropdowns: true,
      showSentence: false,
      showColor: false,
      sentence: null,
      monochromeImage: null,
      colorImage: null,
      selectedAnswers: {}
    };
  },

  methods: {
    getFilename(numericSuffix) {
      return `${this.voiceInstructionsPrefix}/buildasentence-${this.voice}-${numericSuffix}.mp3`;
    },
    madeAllSelections() {
      return this.currentRound.dropdowns.length === Object.keys(this.selectedAnswers).length;
    },

    handleNewRound() {
      this.showDropdowns = true;
      this.showColor = false;
      this.showSentence = false;
      this.activeDropdown = null;
      this.selectedAnswers = {};
      this.monochromeImage = null;
      this.colorImage = null;
      this.sentence = null;

      this.hideKeyboard();
      TweenMax.delayedCall(0.5, this.staggerDropdowns);
    },

    onDropdownOpen(id, collapsed) {
      this.collapsed = collapsed;
      this.activeDropdown = id;
    },

    onDropdownSelect({ dropDownId, option }) {
      this.activeDropdown = null;
      this.selectedAnswers[dropDownId] = option;

      const { dropdowns } = this.currentRound;
      const { answers } = dropdowns.find(({ id }) => dropDownId === `${id}`);
      const answer = answers.find(({ answer }) => answer === option);

      if (answer.monochromeImage) this.monochromeImage = answer.monochromeImage.image.src;
      if (answer.colorImage) this.colorImage = answer.colorImage.image.src;

      if (this.madeAllSelections()) {
        this.sentence = `${this.currentRound.dropdowns
          .map(({ id }) => this.selectedAnswers[id])
          .join(' ')}.`;

        TweenMax.delayedCall(1, () => {
          this.showDropdowns = false;
          this.resetCompletedSteps();
          this.overwriteProgressText({
            currentText: this.sentence
          });
          this.showKeyboard({ enable: true });
          TweenMax.delayedCall(1, () => {
            this.setContainerHeight();
            this.showSentence = true;
            this.cache.get(this.getFilename(2)).play();
          });
        });
      }
    },

    async loadActivity() {
      this.voice = this.getRandomVoice();
      const activityState = this.getActivityState();
      this.createRounds(activityState);

      await this.loadInstructions();
      await this.loadImages();
    },

    async loadImages() {
      this.rounds.forEach(({ dropdowns }) => {
        dropdowns.forEach(({ answers }) => {
          answers.forEach(({ monochromeImage, colorImage }) => {
            if (monochromeImage) this.queueImage(monochromeImage.image.src);
            if (colorImage) this.queueImage(colorImage.image.src);
          });
        });
      });
      return this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Use the drop-down menus to build a sentence.',
          voice: this.getFilename(1),
          delay: 4
        }
      };
    },

    async loadInstructions() {
      this.queueAudio(this.getFilename(2));
      return this.downloadAudio();
    },

    setContainerHeight() {
      const kbWrapper = document.querySelector('.phoenix-keyboard-wrapper');
      this.$refs.buildASentenceDOM.style.height = `calc(100% - ${kbWrapper.offsetHeight}px)`;
    },

    staggerDropdowns() {
      const staggerFrom = { opacity: 0, x: -10 };
      const staggerTo = { opacity: 1, x: 0 };
      const kwtDOMElements = document.querySelectorAll('.bas-dropdown');
      TweenMax.staggerFromTo(kwtDOMElements, 0.25, staggerFrom, staggerTo, 0.2);
    },

    startActivity() {
      this.hideKeyboard();
      this.setupNewRound();
      this.errorLoggerStart();
      this.$nextTick(() => {
        this.staggerDropdowns();
      });
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();

      if (this.isEndOfCurrentSentence) {
        this.showColor = true;
        this.sndBell();
        this.completeStep();
        this.setCurrentStep();

        if (this.isEndOfActivity) {
          TweenMax.delayedCall(1.5, this.completeActivity);
          return;
        }

        TweenMax.delayedCall(1, () => {
          this.$refs.buildASentenceDOM.style.height = '100%';
          this.handleNewRound();
          this.setupNewRound();
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.option-container {
  width: 200px;
  position: relative;
  margin-top: 20px;

  &:not(:first-child) {
    margin-left: 20px;
  }
}

.text,
.sentence {
  font-size: 28px;
  text-align: left;
  font-family: 'hwtfutura';
}

.complete-sentence {
  display: flex;
  height: 100%;

  .left {
    padding-left: 30px;
    padding-right: 10px;
    flex-basis: 55%;
  }

  .right {
    padding-left: 10px;
    padding-right: 30px;
    flex-basis: 45%;
  }

  .left,
  .right {
    display: flex;
    box-sizing: border-box;
    justify-content: center;
    align-items: center;
  }

  .right {
    position: relative;
  }
}

.sentence-image-monochrome,
.sentence-image-color {
  transform: translate(-50%, -50%);
  position: absolute;
  max-width: 100%;
  z-index: 100;
  height: auto;
  left: 50%;
  top: 50%;
}

.sentence-image-color {
  z-index: 110;
}

.build-a-sentence {
  width: 100%;
  height: 100%;
  position: relative;
  text-align: center;

  .dropdowns {
    position: absolute;
    transform: translate(-50%, -50%);
    box-sizing: border-box;
    padding-right: 20px;
    padding-left: 20px;
    width: 100%;
    left: 50%;
    top: 50%;

    .option-container {
      position: relative;
      display: inline-block;

      .bas-dropdown {
        width: 200px;
      }
    }
  }
}
</style>
